import React from 'react'
import classNames from 'classnames/bind'
import { withLocalization, Text } from 'hocs/localization'
import { getCurrencySymbol } from 'utils/user'
import * as styles from './LeaseAgreement.scss'

const cx = classNames.bind(styles)

const LeaseAgreement: React.SFC<any> = ({
  t,
  productName,
  customerShippingAddress,
  monthlyFee,
  monthlyPayment,
  contractId,
  activationFee,
  businessSignatory,
  businessSignatoryTitle,
  customerName,
  customerPhoneNumber,
  customerEmail,
  contractStartDate,
  contractEndDate,
  firstPaymentDate,
  monthlyPaymentDay,
  totalMonthlyPayment,
  otherCharges,
  totalCost,
  totalTax,
  upDownFee,
}) => {
  const currency = getCurrencySymbol()

  return (
    <React.Fragment>
      <div className={styles.summary}>
        <div className={cx('col-md-12', styles.container768)}>
          <div className={styles.selectedPlan}>
            <h2>{t('leaseSummary')}</h2>
          </div>
          <div className={styles.summaryTable}>
            <div className={styles.summaryLine}>
              <div className={styles.summaryCondition}>{t('deviceLabel')}</div>
              <div className={styles.summaryValue}>{productName}</div>
            </div>
            <div className={styles.summaryLine}>
              <div className={styles.summaryCondition}>{t('shippingAddressLabel')}</div>
              <div className={styles.summaryValue}>{customerShippingAddress}</div>
            </div>
            <div className={styles.summaryLine}>
              <div className={styles.summaryCondition}>{t('startContractLabel')}</div>
              <div className={styles.summaryValue}>{contractEndDate}</div>
            </div>
            {activationFee !== '0.00' && (
              <div className={styles.summaryLine}>
                <div className={styles.summaryCondition}>{t('activationFeeLabel')}</div>
                <div className={styles.summaryValue}>{`${currency}${activationFee}`}</div>
              </div>
            )}
            <div className={styles.summaryLine}>
              <div className={styles.summaryCondition}>{t('monthlyPaymentLabel')}</div>
              <div className={styles.summaryValue}>{`${currency}${monthlyFee}`}</div>
            </div>
            <div className={styles.summaryLine}>
              <div className={styles.summaryCondition}>{t('contractTermLabel')}</div>
              <div className={styles.summaryValue}>{t('contractTermValue')}</div>
            </div>
            <div className={styles.summaryLine}>
              <div className={styles.summaryCondition}>{t('securityDeposit')}</div>
            </div>
          </div>
          <div className={styles.summaryLine}>{t('summaryInfo1')}</div>
          <div className={styles.summaryLine}>{t('summaryInfo2')}</div>
          <div className={styles.summaryLine}>{t('summaryInfo3')}</div>
        </div>
      </div>
      <div className={styles.terms}>
        <div className={cx('text-center', styles.selectedPlan)}>
          <h2>{t('review')}</h2>
        </div>
        <div className={cx('col-md-12', styles.container768, styles.termsDescription)}>
          {t('leaseAgreementInfo')}
        </div>
        <div className={styles.container1200}>
          <div className={styles.termsBlock}>
            <div className={styles.termsSection}>
              <div className={styles.section}>{t('section1')}</div>
            </div>
            <div className={styles.termsContent}>
              <div className={styles.summaryTable}>
                <div className={styles.summaryLine}>
                  <div className={styles.summaryCondition}>{t('transactionIDLabel')}</div>
                  <div className={styles.summaryValue}>{contractId}</div>
                </div>
                <div className={styles.summaryLine}>
                  <div className={styles.summaryCondition}>{t('startDateLabel')}</div>
                  <div className={styles.summaryValue}>{contractStartDate}</div>
                </div>
              </div>
              <div className={styles.summaryTable}>
                <div className={styles.summaryLine}>{t('epsonNote')}</div>
                <div className={styles.summaryLine}>
                  <div className={styles.summaryCondition}>{t('addressCondition')} </div>
                  <div className={styles.summaryValue}>{t('epsonAddress')}</div>
                </div>
              </div>
              <div className={styles.summaryTable}>
                <div className={styles.summaryLine}>{t('customerNote')}</div>
                <div className={styles.summaryLine}>
                  <div className={styles.summaryCondition}>{t('customerNameLabel')}</div>
                  <div className={styles.summaryValue}>{businessSignatory
                    ? `${businessSignatory}, ${businessSignatoryTitle}`
                    : customerName
                  }</div>
                </div>
                <div className={styles.summaryLine}>
                  <div className={styles.summaryCondition}>{t('mailingAddressLabel')}</div>
                  <div className={styles.summaryValue}>{customerShippingAddress}</div>
                </div>
                <div className={styles.summaryLine}>
                  <div className={styles.summaryCondition}>{t('phoneLabel')}</div>
                  <div className={styles.summaryValue}>{customerPhoneNumber}</div>
                </div>
                <div className={styles.summaryLine}>
                  <div className={styles.summaryCondition}>{t('emailLabel')}</div>
                  <div className={styles.summaryValue}>{customerEmail}</div>
                </div>
              </div>
              <div className={styles.summaryLine}>
                <div className={styles.summaryCondition}>{t('deviceDescriptionLabel')}</div>
                <div className={styles.summaryValue}>{productName}</div>
              </div>
            </div>
          </div>
          <div className={styles.termsBlock}>
            <div className={styles.termsSection}>
              <div className={styles.section}>{t('section2')}</div>
            </div>
            <div className={styles.termsContent}>
              <div className={styles.sectionTable}>
                <div className={styles.sectionColumn}>
                  <div className={styles.tableTitle}>{t('columnTitle1')}</div>
                  <div>
                    <div>{t('firstPaymentLabel')}</div>
                    <div className={styles.bold}>{`${currency}${monthlyPayment}`}</div>
                  </div>
                </div>
                <div className={styles.sectionColumn}>
                  <div className={styles.tableTitle}>{t('columnTitle2')}</div>
                  <div className={styles.tableLine}>
                    {t('monthlyPayment1')}<span>{`${currency}${monthlyPayment}`}</span>
                    {t('monthlyPayment2')}<span>{firstPaymentDate}</span>
                    {t('monthlyPayment3')}<span>{`${currency}${monthlyPayment}`}</span>
                    {t('monthlyPayment4')}<span>{monthlyPaymentDay}</span>
                    {t('monthlyPayment5')}<span>{`${currency}${totalMonthlyPayment}.`}</span>
                  </div>
                </div>
                <div className={styles.sectionColumn}>
                  <div className={styles.tableTitle}>{t('columnTitle3')}</div>
                  <div className={styles.tableLine}>{t('otherChargesInfo')}</div>
                  <div className={styles.bold}>{otherCharges}</div>
                  {(!upDownFee || upDownFee !== '$0.00') && (
                     <div className={styles.bold}>{upDownFee}</div>
                  )}
                </div>
                <div className={styles.sectionColumn}>
                  <div className={styles.tableTitle}>{t('columnTitle4')}</div>
                  <div className={styles.tableLine}>{t('totalInfo')}</div>
                  <div className={styles.bold}>{`${currency}${totalCost}`}</div>
                </div>
              </div>
              <p>{t('section2Info1')}<strong>{t('section2Info1Bold')}</strong></p>
              <p>{t('section2Info2')}<strong>{t('section2Info2Bold')}</strong></p>
            </div>
          </div>
          <div className={styles.termsBlock}>
            <div className={styles.termsSection}>
              <div className={styles.section}>{t('section3')}</div>
            </div>
            <div className={styles.termsContent}>
              <div className={styles.bold}>{t('leaseTermTitle')}</div>
              <p>{t('leaseTermContent1')}<span>{contractEndDate}</span>{t('leaseTermContent2')}</p>
              <div className={styles.bold}>{t('leaseTermNote')}</div>
              <div className={styles.bold}>{t('taxesTitle')}</div>
              <p>{t('taxesContent1')}<span>{`${currency}${totalTax}`}</span>{t('taxesContent2')}</p>
              <p>{t('taxesContent3')}</p>
              <div className={styles.bold}>{t('paymentMethodTitle')}</div>
              <p>{t('paymentMethodContent1')}</p>
              <p>{t('paymentMethodContent5')}</p>
            </div>
          </div>
          <div className={styles.termsBlock}>
            <div className={styles.termsSection}>
              <div className={styles.section}>{t('section4')}</div>
            </div>
            <div className={styles.termsContent}>
              <div className={styles.bold}>{t('internetConnectionTitle')}</div>
              <p>{t('internetConnectionContent')}</p>
              <div className={styles.bold}>{t('addressTitle')}</div>
              <p>{t('addressContent')}</p>
              <div className={styles.bold}>{t('maintenanceTitle')}</div>
              <p>{t('maintenanceContent')}</p>
              <div className={styles.bold}>{t('providedInkTitle')}</div>
              <p>{t('providedInkContent')}</p>
              <div className={styles.bold}>{t('insuranceTitle')}</div>
              <p>{t('insuranceContent')}</p>
              <div className={styles.bold}>{t('printerLossTitle')}</div>
              <p>{t('printerLossContent')}</p>
              <div className={styles.bold}>{t('warrantiesTitle')}</div>
              <p>{t('warrantiesContent1')}</p>
              <p>{t('warrantiesContent2')}</p>
            </div>
          </div>
          <div className={styles.termsBlock}>
            <div className={styles.termsSection}>
              <div className={styles.section}>{t('section5')}</div>
            </div>
            <div className={styles.termsContent}>
              <div className={styles.bold}>{t('leaseOptionsTitle')}</div>
              <p>{t('leaseOptionsContent1')}</p>
              <p>{t('leaseOptionsContent2')}</p>
              <p>{t('leaseOptionsContent3')}</p>
              <p>{t('leaseOptionsContent4')}</p>
              <p>{t('leaseOptionsContent5')}</p>
              <div className={styles.bold}>{t('monthToMonthTitle')}</div>
              <p>{t('monthToMonthContent1')}</p>
              <div className={styles.bold}>{t('printerReturningTitle')}</div>
              <p>{t('printerReturningContent')}</p>
              <div className={styles.bold}>{t('wearTitle')}</div>
              <p>{t('wearContent1')}</p>
              <p>{t('wearContent2')}</p>
              <p>{t('wearContent3')}</p>
              <p>{t('wearContent4')}</p>
              <div className={styles.bold}>{t('terminationTitle1')}</div>
              <p>{t('terminationContent11')}</p>
              <p>{t('terminationContent12')}</p>
              <p>{t('terminationContent13')}</p>
              <p>{t('terminationContent14')}</p>
              <div className={styles.bold}>{t('terminationTitle2')}</div>
              <p>{t('terminationContent21')}</p>
              <div className={styles.bold}>{t('chargeCalculationTitle')}</div>
              <p>{t('chargeCalculationContent')}</p>
              <div className={styles.bold}>{t('defaultTitle')}</div>
              <p>{t('defaultContent1')}</p>
              <p>{t('defaultContent2')}</p>
              <p>{t('defaultContent3')}</p>
              <p>{t('defaultContent4')}</p>
              <p>{t('defaultContent5')}</p>
              <div className={styles.bold}>{t('remediesTitle')}</div>
              <p>{t('remediesContent1')}</p>
              <p>{t('remediesContent2')}</p>
              <p>{t('remediesContent3')}</p>
              <p>{t('remediesContent4')}</p>
              <p>{t('remediesContent5')}</p>
              <p>{t('remediesContent6')}</p>
              <p>{t('remediesContent7')}</p>
              <p>{t('remediesContent8')}</p>
              <div className={styles.bold}>{t('paymentTitle')}</div>
              <p>{t('paymentContent')}</p>
            </div>
          </div>
          <div className={styles.termsBlock}>
            <div className={styles.termsSection}>
              <div className={styles.section}>{t('section6')}</div>
            </div>
            <div className={styles.termsContent}>
              <div className={styles.bold}>{t('provisionsTitle')}</div>
              <p>{t('provisionsContent')}</p>
              <div className={styles.bold}>{t('assignmentTitle')}</div>
              <p>{t('assignmentContent')}</p>
              <div className={styles.bold}>{t('noticesTitle')}</div>
              <p>{t('noticesContent1')}</p>
              <p>{t('noticesContent3')}</p>
              <p>{t('noticesContent4')}</p>
              <p>{t('noticesContent5')}</p>
              <p>{t('noticesContent6')}</p>
              <div className={styles.bold}>{t('priceAndPlanChangesTitle')}</div>
              <p>{t('priceAndPlanChanges')}</p>
            </div>
          </div>
          <div className={styles.termsBlock}>
            <div className={styles.notes}>
              <p>{t('info1')}</p>
              <p>{t('info2')}</p>
              <p>{t('info3')}</p>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

export default withLocalization('leaseAgreement')(LeaseAgreement)
